import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import api, { type ApiError, type SignupActivationSession } from '../../apiService';
import { ACCOUNT_EXISTS_USER_MESSAGE, ORGANIZATION_SERVED_USER_MESSAGE } from '../../apiErrors';
import DialogShell from '../ui/DialogShell';
import InAppLink from '../ui/InAppLink';
import { BARK_WEBSITE_URL, CONNECTOR_GUIDE_URL } from '../../utils/moduleAccess';
import { ROUTE_PATTERNS } from '../../router/routes';

interface ForgotPasswordModalProps {
    onClose: () => void;
    initialToken?: string;
    /** "reset" (default) — user-initiated forgot-password. "invite" —
     *  admin-initiated onboarding with welcome copy. BAR-126 decision D10.
     *  "signup" — BAR-1528 free-signup activation: the password is set and the
     *  account created in one call, and success points to the connector guide. */
    mode?: 'reset' | 'invite' | 'signup';
    /** Signup mode only. When the activation answers with a `session`, the
     *  person is signed in through this, the same callback LoginPage calls
     *  after `/login`, and the modal closes. Leave it out where someone is
     *  already signed in: an activation link never replaces that session. */
    onLoginSuccess?: (token: string, userData?: SignupActivationSession['user'], refreshToken?: string) => void;
    /** Signup mode only, for a signed-in viewer: the success view links the
     *  in-app connect page (`/connect`) through this, in place of the website
     *  guide. */
    onOpenConnectGuide?: () => void;
    /** Reset and invite modes: called in place of `onClose` once the success
     *  message has shown (BAR-1564: the Keycloak sign-in page follows). */
    onPasswordSet?: () => void;
}

/** A session the app can sign in with: an access token and a user. */
function usableSession(session: SignupActivationSession | undefined): session is SignupActivationSession {
    return Boolean(
        session
        && typeof session.access_token === 'string'
        && session.access_token
        && session.user
        && typeof session.user === 'object',
    );
}

/** Where a signup activation ended. Each is terminal: none offers the form again. */
type SignupOutcome = 'activated' | 'invalid' | 'served' | 'exists';

// The link idiom of moduleAccess.tsx. On the external links below,
// `rel="noopener noreferrer"` keeps the opened tab from reaching back through
// `window.opener`.
const linkClass = 'text-accent hover:text-accent-hover underline';

function ForgotPasswordModal({
    onClose,
    initialToken,
    mode = 'reset',
    onLoginSuccess,
    onOpenConnectGuide,
    onPasswordSet,
}: ForgotPasswordModalProps) {
    const titleId = React.useId();
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const invalidTokenActionRef = useRef<HTMLButtonElement>(null);
    const signupActionRef = useRef<HTMLButtonElement>(null);
    const [email, setEmail] = useState('');
    const resetToken = initialToken ?? '';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [step, setStep] = useState<'email' | 'reset'>(initialToken ? 'reset' : 'email');
    const [isInviteTokenInvalid, setIsInviteTokenInvalid] = useState(false);
    const [signupOutcome, setSignupOutcome] = useState<SignupOutcome | null>(null);
    const [activatedEmail, setActivatedEmail] = useState('');

    const isInvite = mode === 'invite';
    const isSignup = mode === 'signup';

    useEffect(() => {
        if (step !== 'email' || !initialToken) return;
        const frame = window.requestAnimationFrame(() => initialFocusRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [initialToken, step]);

    useEffect(() => {
        if (!isInviteTokenInvalid) return;
        const frame = window.requestAnimationFrame(() => invalidTokenActionRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [isInviteTokenInvalid]);

    // The form is gone once activation ends either way: focus its one action.
    useEffect(() => {
        if (!signupOutcome) return;
        const frame = window.requestAnimationFrame(() => signupActionRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [signupOutcome]);

    const handleRequestReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        try {
            const result = await api.forgotPassword(email);
            setMessage(result.message || 'If that email exists, a reset link has been sent.');
            setIsSuccess(true);
        } catch (error: any) {
            const apiError = error as ApiError;
            const userMessage = apiError.status === undefined
                ? ''
                : (apiError.userMessage ?? apiError.message);
            setMessage(userMessage || 'Failed to request password reset');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        if (newPassword !== confirmPassword) {
            setMessage('Passwords do not match');
            setIsLoading(false);
            return;
        }

        if (newPassword.length < 8) {
            setMessage('Password must be at least 8 characters');
            setIsLoading(false);
            return;
        }

        try {
            const result = await api.resetPassword(resetToken, newPassword);
            const successMessage = isInvite
                ? 'Your account is ready. Sign in to start.'
                : (result.message || 'Password updated.');
            setMessage(successMessage);
            setIsSuccess(true);
            setTimeout(() => { (onPasswordSet ?? onClose)(); }, 2000);
        } catch (error: any) {
            const apiError = error as ApiError;
            if (apiError.code === 'invalid_reset_token') {
                setNewPassword('');
                setConfirmPassword('');
                if (isInvite) {
                    setIsInviteTokenInvalid(true);
                    setMessage('This activation link is invalid or expired. Ask an administrator for a new invitation.');
                } else {
                    setStep('email');
                    setMessage('This reset link is invalid or expired. Enter your email to request a new link.');
                }
            } else {
                const userMessage = apiError.status === undefined
                    ? ''
                    : (apiError.userMessage ?? apiError.message);
                setMessage(
                    userMessage
                    || (isInvite ? 'Failed to activate account' : 'Failed to reset password'),
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    // BAR-1528: the free-signup activation. The same password form, but the
    // call creates the account, and every ending is terminal: activated (sign
    // the person in when the server sent a session, else point to the
    // connector guide), an unusable link (register again on the website; no
    // administrator issues these), a domain a customer already uses (the
    // account is theirs to get from that organization), or an account made
    // for the address since the email went out (sign in).
    const handleActivateSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        if (newPassword !== confirmPassword) {
            setMessage('Passwords do not match');
            setIsLoading(false);
            return;
        }

        if (newPassword.length < 8) {
            setMessage('Password must be at least 8 characters');
            setIsLoading(false);
            return;
        }

        try {
            const result = await api.activateSignup(resetToken, newPassword);
            setNewPassword('');
            setConfirmPassword('');
            const session = result?.session;
            if (onLoginSuccess && usableSession(session)) {
                // Signed in, the same way /login signs in. The caller opens
                // the connect page; this modal has nothing left to say.
                onLoginSuccess(session.access_token, session.user, session.refresh_token || undefined);
                onClose();
                return;
            }
            setActivatedEmail(typeof result?.email === 'string' ? result.email : '');
            setIsSuccess(true);
            setSignupOutcome('activated');
        } catch (error: any) {
            const apiError = error as ApiError;
            const outcome: SignupOutcome | null = apiError.code === 'activation_invalid'
                ? 'invalid'
                : apiError.code === 'organization_served'
                ? 'served'
                : apiError.code === 'account_exists'
                ? 'exists'
                : null;
            if (outcome) {
                setNewPassword('');
                setConfirmPassword('');
                setSignupOutcome(outcome);
            } else {
                const userMessage = apiError.status === undefined
                    ? ''
                    : (apiError.userMessage ?? apiError.message);
                setMessage(userMessage || 'Could not activate your account. Try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const heading = isSignup && signupOutcome === 'activated'
        ? 'Your BARK account is ready'
        : isSignup && signupOutcome === 'invalid'
        ? 'Activation link unavailable'
        : isSignup && signupOutcome === 'served'
        ? 'Your organisation already uses BARK'
        : isSignup && signupOutcome === 'exists'
        ? 'You already have a BARK account'
        : isInviteTokenInvalid
        ? 'Invitation link unavailable'
        : step === 'email'
        ? 'Reset your password'
        : isSignup
        ? 'Activate your BARK account'
        : (isInvite ? 'Welcome to BARK — set your password' : 'Set new password');

    const description = isSignup
        ? 'Choose a password to activate your account.'
        : isInvite
        ? "You've been invited to join BARK. Choose a password to activate your account."
        : 'Enter your new password below.';

    const activates = isInvite || isSignup;
    const submitLabel = activates ? 'Activate account' : 'Reset password';
    const loadingLabel = step === 'email'
        ? 'Sending reset link'
        : (activates ? 'Activating account' : 'Resetting password');
    const loadingText = step === 'email'
        ? 'Sending…'
        : (activates ? 'Activating…' : 'Resetting…');
    const closeLabel = activates ? 'Close account activation' : 'Close password reset';

    return (
        <DialogShell
            open
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy={!isSuccess && (email !== '' || newPassword !== '' || confirmPassword !== '') ? 'static' : 'dismiss'}
            initialFocusRef={initialFocusRef}
            overlayClassName="flex items-center justify-center p-4"
            panelClassName="w-full max-w-md relative overflow-hidden"
        >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={closeLabel}
                    className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:bg-gray-100"
                >
                    <X size={24} strokeWidth={1.5} aria-hidden="true" />
                </button>
                <div className="p-8">
                    <h2 id={titleId} className="text-center text-xl font-semibold tracking-tight text-gray-900">
                        {heading}
                    </h2>
                    {message && (
                        <p
                            key={isSuccess ? 'status' : 'alert'}
                            role={isSuccess ? 'status' : 'alert'}
                            aria-atomic="true"
                            className={`text-center text-sm mt-4 ${isSuccess ? 'text-success' : 'text-danger'}`}
                        >
                            {message}
                        </p>
                    )}

                    {!isSuccess && step === 'email' && (
                        <form onSubmit={handleRequestReset} className="mt-8 space-y-6">
                            <p className="text-sm text-gray-600 text-center">
                                Enter your email address and we'll send you a link to reset your password.
                            </p>
                            <div className="rounded-md">
                                <input
                                    ref={initialFocusRef}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    aria-label="Email address"
                                    required
                                    className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="Email address"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                aria-label={isLoading ? loadingLabel : undefined}
                                className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                            >
                                {isLoading && (
                                    <Loader2
                                        size={16}
                                        strokeWidth={1.5}
                                        className="mr-2 animate-spin"
                                        aria-hidden="true"
                                    />
                                )}
                                {isLoading ? loadingText : 'Send reset link'}
                            </button>
                        </form>
                    )}

                    {!isSuccess && step === 'reset' && !isInviteTokenInvalid && !signupOutcome && (
                        <form onSubmit={isSignup ? handleActivateSignup : handleResetPassword} className="mt-8 space-y-6">
                            <p className="text-sm text-gray-600 text-center">
                                {description}
                            </p>
                            <div className="rounded-md -space-y-px">
                                <input
                                    ref={initialFocusRef}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    type="password"
                                    name="new-password"
                                    autoComplete="new-password"
                                    aria-label="New password"
                                    required
                                    minLength={8}
                                    className="relative block w-full appearance-none rounded-t-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="New password"
                                />
                                <input
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    type="password"
                                    name="confirm-password"
                                    autoComplete="new-password"
                                    aria-label="Confirm new password"
                                    required
                                    minLength={8}
                                    className="relative block w-full appearance-none rounded-b-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm"
                                    placeholder="Confirm new password"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                aria-label={isLoading ? loadingLabel : undefined}
                                className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-border-default focus:ring-offset-2 disabled:bg-fg-tertiary transition-colors"
                            >
                                {isLoading && (
                                    <Loader2
                                        size={16}
                                        strokeWidth={1.5}
                                        className="mr-2 animate-spin"
                                        aria-hidden="true"
                                    />
                                )}
                                {isLoading ? loadingText : submitLabel}
                            </button>
                        </form>
                    )}
                    {!isSuccess && isInviteTokenInvalid && (
                        <button
                            ref={invalidTokenActionRef}
                            type="button"
                            onClick={onClose}
                            className="mt-8 flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-on-primary hover:bg-accent-hover focus-visible:outline-none focus-visible:shadow-bark-focus transition-colors"
                        >
                            Close
                        </button>
                    )}
                    {signupOutcome && (
                        <>
                            {signupOutcome === 'activated' && (
                                <div role="status" aria-atomic="true" className="mt-4 space-y-3 text-sm text-fg-secondary text-center">
                                    {onOpenConnectGuide ? (
                                        // Someone is signed in here: the steps are on
                                        // the connect page of this app.
                                        <p>
                                            BARK works inside Claude, ChatGPT and Codex.{' '}
                                            <InAppLink
                                                href={ROUTE_PATTERNS.connect}
                                                onNavigate={onOpenConnectGuide}
                                                className={linkClass}
                                            >
                                                See how to connect it to your assistant
                                            </InAppLink>
                                            .
                                        </p>
                                    ) : (
                                        <p>
                                            BARK works inside Claude, ChatGPT and Codex. Follow the{' '}
                                            <a
                                                href={CONNECTOR_GUIDE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={linkClass}
                                            >
                                                connector guide
                                            </a>{' '}
                                            to connect it to your assistant.
                                        </p>
                                    )}
                                    {activatedEmail && (
                                        <p>Sign in with {activatedEmail} when your assistant asks.</p>
                                    )}
                                </div>
                            )}
                            {signupOutcome === 'invalid' && (
                                <p role="alert" aria-atomic="true" className="text-center text-sm mt-4 text-danger">
                                    This activation link is invalid or has expired. Register again on the{' '}
                                    <a
                                        href={BARK_WEBSITE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={linkClass}
                                    >
                                        website
                                    </a>{' '}
                                    to get a new link.
                                </p>
                            )}
                            {signupOutcome === 'served' && (
                                <p role="alert" aria-atomic="true" className="text-center text-sm mt-4 text-danger">
                                    {ORGANIZATION_SERVED_USER_MESSAGE}
                                </p>
                            )}
                            {signupOutcome === 'exists' && (
                                <p role="status" aria-atomic="true" className="text-center text-sm mt-4 text-fg-secondary">
                                    {ACCOUNT_EXISTS_USER_MESSAGE}
                                </p>
                            )}
                            <button
                                ref={signupActionRef}
                                type="button"
                                onClick={onClose}
                                className="mt-8 flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-on-primary hover:bg-accent-hover focus-visible:outline-none focus-visible:shadow-bark-focus transition-colors"
                            >
                                Close
                            </button>
                        </>
                    )}
                </div>
        </DialogShell>
    );
}

export default ForgotPasswordModal;
